import { computeBudget, rawNumber, summarizeProject, type Project } from '@bigint-project/core';

import { formatDuration } from '../grid/duration.js';
import { formatHours, formatMoney, formatMoneyOrDash, formatPercent, formatRatio } from '../resource/format.js';
import {
  hasBaseline,
  progressAsOf,
  selectProjectProgressSummary,
  selectCurrentCostResult,
  selectCostResult,
  selectEvmResult,
  selectOverallocations,
  selectOverallocatedResourceIds,
} from '../../integration/derivations.js';

export interface ProjectOverviewProps {
  readonly project: Project;
  /** 由宿主決定列印媒介（瀏覽器、Tauri 或系統列印服務）。 */
  readonly onPrint: () => void;
}

function formatDate(value: string): string {
  return value.slice(0, 10);
}

function formatWork(minutes: number): string {
  return formatHours(minutes / 60) || '0h';
}

function projectStatus(taskCount: number, completedTaskCount: number): string {
  if (taskCount === 0) return '尚無任務';
  if (completedTaskCount === taskCount) return '已完成';
  if (completedTaskCount === 0) return '尚未開始';
  return '進行中';
}

function Metric({ label, value, testId }: { readonly label: string; readonly value: string; readonly testId?: string }): React.JSX.Element {
  return (
    <div className="rounded border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900 print:border-slate-300 print:p-2">
      <dt className="text-xs text-slate-500 dark:text-slate-400">{label}</dt>
      <dd data-testid={testId} className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">{value}</dd>
    </div>
  );
}

/**
 * 專案摘要的純展示元件。
 *
 * 成本、EVM 與過度分配一律透過整合層 selector 取得；這可沿用現有 memo、引擎
 * 錯誤降級和 Baseline 0 語意，而不是在報表內複製商業規則。
 */
export function ProjectOverview({ project, onPrint }: ProjectOverviewProps): React.JSX.Element {
  const summary = summarizeProject(project);
  const budget = computeBudget(project);
  const hasBudgetCost = project.budgetAssignments?.some((assignment) => assignment.budgetCost !== undefined) ?? false;
  const hasBudgetWork = project.budgetAssignments?.some((assignment) => assignment.budgetWork !== undefined) ?? false;
  const baselineReady = hasBaseline(project);
  const baselineCost = baselineReady ? selectCostResult(project) : null;
  const cost = baselineCost ?? selectCurrentCostResult(project);
  const evm = baselineReady ? selectEvmResult(project) : null;
  const overallocations = selectOverallocations(project);
  const overallocatedResourceIds = selectOverallocatedResourceIds(project);
  const executableTasks = project.tasks.filter((task) => !task.isSummary);
  const completedTaskCount = executableTasks.filter((task) => task.percentComplete >= 100).length;
  const status = projectStatus(executableTasks.length, completedTaskCount);
  const completionPercent = executableTasks.length === 0 ? 0 : (completedTaskCount / executableTasks.length) * 100;
  const costVariance = baselineCost?.total.variance;
  const progress = selectProjectProgressSummary(project, progressAsOf(project));

  return (
    <article data-testid="project-overview" className="mx-auto w-full max-w-6xl bg-slate-50 p-4 text-slate-900 dark:bg-slate-950 dark:text-slate-100 print:max-w-none print:bg-white print:p-0">
      <header className="flex flex-wrap items-start justify-between gap-4 border-b border-slate-200 pb-4 dark:border-slate-700 print:border-slate-400">
        <div>
          <p className="text-sm text-slate-500 dark:text-slate-400">專案總覽</p>
          <h1 className="mt-1 text-2xl font-bold">{project.name}</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {formatDate(summary.start as string)} 至 {formatDate(summary.finish as string)} ・ {status}
          </p>
        </div>
        <button type="button" onClick={onPrint} className="rounded border border-slate-300 px-3 py-1.5 text-sm font-medium hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800 print:hidden">
          列印報表
        </button>
      </header>

      <section aria-labelledby="project-overview-summary" className="mt-5">
        <h2 id="project-overview-summary" className="text-base font-semibold">專案摘要</h2>
        <dl className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Metric label="專案狀態" value={status} testId="project-overview-status" />
          <Metric label="任務完成" value={`${completedTaskCount} / ${executableTasks.length} (${formatPercent(completionPercent)})`} testId="project-overview-completion" />
          <Metric label="總工期" value={formatDuration(summary.totalDuration)} />
          <Metric label="總工時" value={formatWork(rawNumber(summary.totalWork))} />
          <Metric label="總成本" value={cost === null ? '—' : formatMoney(rawNumber(cost.total.cost), project.currency)} testId="project-overview-cost" />
          <Metric label="資源數" value={String(project.resources.length)} />
          <Metric label="過度分配資源" value={String(overallocatedResourceIds.size)} testId="project-overview-overallocated-resources" />
          <Metric label="過度分配區間" value={String(overallocations.length)} testId="project-overview-overallocations" />
        </dl>
        <p
          data-testid="project-overview-progress-status"
          className={`mt-3 rounded px-3 py-2 text-sm ${progress.counts.behind > 0 ? 'bg-danger-soft text-danger' : 'bg-raised text-fg-muted'}`}
        >
          進度狀態：落後 {progress.counts.behind} 項 ・ 如期 {progress.counts.onTrack} ・ 超前 {progress.counts.ahead} ・ 未開始 {progress.counts.notStarted}
        </p>
      </section>

      <section aria-labelledby="project-overview-budget" className="mt-5">
        <h2 id="project-overview-budget" className="text-base font-semibold">專案預算</h2>
        {!hasBudgetCost && !hasBudgetWork ? (
          <p data-testid="project-overview-no-budget" className="mt-2 rounded border border-dashed border-slate-300 p-3 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">
            尚未設定預算資源；請至資源工作表建立專案預算。
          </p>
        ) : (
          <dl data-testid="project-overview-budget-metrics" className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Metric label="預算成本" value={hasBudgetCost ? formatMoney(rawNumber(budget.totalCost), project.currency) : '—'} testId="project-overview-budget-cost" />
            <Metric label="成本對預算差異" value={!hasBudgetCost || cost === null ? '—' : formatMoney(rawNumber(cost.total.cost) - rawNumber(budget.totalCost), project.currency)} testId="project-overview-budget-cost-variance" />
            <Metric label="預算工時" value={hasBudgetWork ? formatWork(rawNumber(budget.totalWork)) : '—'} testId="project-overview-budget-work" />
            <Metric label="工時對預算差異" value={hasBudgetWork ? formatWork(rawNumber(summary.totalWork) - rawNumber(budget.totalWork)) : '—'} testId="project-overview-budget-work-variance" />
          </dl>
        )}
      </section>

      <section aria-labelledby="project-overview-baseline" className="mt-5">
        <h2 id="project-overview-baseline" className="text-base font-semibold">Baseline 0 與實獲值</h2>
        {!baselineReady ? (
          <p data-testid="project-overview-no-baseline" className="mt-2 rounded border border-dashed border-slate-300 p-3 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">
            尚未設定 Baseline 0；成本差異與 EVM 指標無法計算。
          </p>
        ) : (
          <dl data-testid="project-overview-baseline-metrics" className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Metric label="成本差異" value={costVariance === null || costVariance === undefined ? '—' : formatMoneyOrDash(rawNumber(costVariance), project.currency)} testId="project-overview-cost-variance" />
            <Metric label="時程差異（SV）" value={evm === null ? '—' : formatMoney(rawNumber(evm.project.sv), project.currency)} />
            <Metric label="成本差異（CV）" value={evm === null ? '—' : formatMoney(rawNumber(evm.project.cv), project.currency)} />
            <Metric label="SPI / CPI" value={evm === null ? '—' : `${formatRatio(evm.project.spi)} / ${formatRatio(evm.project.cpi)}`} testId="project-overview-performance" />
          </dl>
        )}
      </section>
    </article>
  );
}

export default ProjectOverview;
